<script setup>
import { ref, onMounted } from 'vue'
import Button from '@/Components/Buttons/Button.vue'
import Link from '@/Components/Links/Link.vue'
import storage from '@/Utils/storage.js'

const preferences = ref(false)
const statistics = ref(false)
const marketing = ref(false)
const wasSuccessful = ref(false)

onMounted(() => {
  try {
    const cookiePermissionsStr = storage.get({
      type: storage.types.cookie,
      name: 'cookiePermissions',
    })

    if (cookiePermissionsStr) {
      const cookiePermissions = JSON.parse(cookiePermissionsStr)
      preferences.value = Boolean(cookiePermissions.preferences)
      statistics.value = Boolean(cookiePermissions.statistics)
      marketing.value = Boolean(cookiePermissions.marketing)
    }
  } catch (error) {
    console.error('Error loading cookie preferences:', error)
  }
})

const savePreferences = () => {
  try {
    const cookiePermissions = JSON.parse(JSON.stringify(storage.categories))

    for (const key in cookiePermissions) {
      if (key === storage.categories.necessary) {
        cookiePermissions[key] = true
      } else if (key === storage.categories.preferences) {
        cookiePermissions[key] = preferences.value
      } else if (key === storage.categories.statistics) {
        cookiePermissions[key] = statistics.value
      } else if (key === storage.categories.marketing) {
        cookiePermissions[key] = marketing.value
      } else {
        cookiePermissions[key] = false
      }
    }

    storage.setCookie(storage.categories.necessary, 'cookiePermissions', JSON.stringify(cookiePermissions), 365)

    storage.setCookie(storage.categories.necessary, 'cookie-notification-acknowledged', 'yes', 365)

    wasSuccessful.value = true
  } catch (error) {
    console.error('Error saving cookie preferences:', error)
  }
}
</script>

<template>
  <div
    class="py-6"
    id="content-block"
  >
    <h2 class="text-2xl font-bold text-indigo">Cookies</h2>

    <p class="text-lg">
      A cookie lets a website recognise your device and store some information about your preferences or interactions.
    </p>

    <h3 class="text-xl font-bold mt-10">Why we use cookies</h3>

    <p>
      We use our website's cookie tracking data to make informed decisions on whether the site is meeting your needs,
      which leads to us making improvements. For example, we monitor how much traffic each part of the site gets and
      work to improve areas which are not being found when they should be.
    </p>

    <div class="bg-gray-100 p-6 my-6 border-l-4 border-blue-500">
      <p>
        The cookies that we set on our website cannot be used to identify you personally.
        <br />
        <Link
          as="a"
          target="_blank"
          href="https://ico.org.uk/for-the-public/online/cookies/"
          class="font-normal"
          aria-label="Links to information commissioners office website cookie article opens in a new window."
        >
          Find out more about how to manage cookies for all websites.
        </Link>
      </p>
    </div>

    <h3 class="text-xl font-bold mt-10">Turn cookies on or off</h3>

    <p>
      We use 3 main types of cookie on our website. You can choose which cookies you're happy for us to use. Any data
      collected is anonymous.
    </p>

    <div class="6 border rounded p-6 bg-white shadow-sm mb-3">
      <form @submit.prevent="savePreferences">
        <h3 class="text-lg font-bold">Cookies needed for the website to work</h3>

        <p>These cookies do things like keep the website secure. They always need to be on.</p>

        <fieldset class="border-b my-3 pb-3">
          <legend class="text-lg font-bold">Cookies that remember your settings</legend>
          <p>These cookies do things like remember pop-ups you've seen, so you're not shown them again.</p>
          <div class="flex items-center">
            <input
              type="radio"
              id="preferences-on"
              name="preferences"
              :value="true"
              v-model="preferences"
              class="mr-2"
            />
            <label for="preferences-on">On</label>
          </div>
          <div class="flex items-center">
            <input
              type="radio"
              id="preferences-off"
              name="preferences"
              :value="false"
              v-model="preferences"
              class="mr-2"
            />
            <label for="preferences-off">Off</label>
          </div>
        </fieldset>

        <fieldset class="border-b my-3 pb-3">
          <legend class="text-lg font-bold">Cookies that measure website use</legend>
          <p>These cookies store information about how you use our website, such as what you click on.</p>
          <div class="flex items-center">
            <input
              type="radio"
              id="statistics-on"
              name="statistics"
              :value="true"
              v-model="statistics"
              class="mr-2"
            />
            <label for="statistics-on">On</label>
          </div>
          <div class="flex items-center">
            <input
              type="radio"
              id="statistics-off"
              name="statistics"
              :value="false"
              v-model="statistics"
              class="mr-2"
            />
            <label for="statistics-off">Off</label>
          </div>
        </fieldset>

        <fieldset class="my-3">
          <legend class="text-lg font-bold">Cookies that help with our communications and marketing</legend>
          <p>
            These cookies may be set by third party websites and do things like measure how you view videos that are on
            our website.
          </p>
          <div class="flex items-center">
            <input
              type="radio"
              id="marketing-on"
              name="marketing"
              :value="true"
              v-model="marketing"
              class="mr-2"
            />
            <label for="marketing-on">On</label>
          </div>
          <div class="flex items-center">
            <input
              type="radio"
              id="marketing-off"
              name="marketing"
              :value="false"
              v-model="marketing"
              class="mr-2"
            />
            <label for="marketing-off">Off</label>
          </div>
        </fieldset>

        <div
          v-if="wasSuccessful"
          id="success-message"
          class="bg-green-100 text-green-800 p-4 font-bold mb-3"
          tabindex="-1"
          role="alert"
        >
          Your cookie preferences have been saved.
        </div>

        <Button type="submit">Save cookie preferences</Button>
      </form>
    </div>

    <h3 class="text-xl font-bold">Google Analytics</h3>

    <p>We use Google Analytics to collect information about how you use our website.</p>

    <p>We store information about:</p>

    <ul class="list-disc ml-5">
      <li>the pages you visit and how long you spend on each page</li>
      <li>how you got to our website</li>
      <li>what you click on while you're visiting our website</li>
    </ul>

    <p>We do not:</p>

    <ul class="list-disc ml-5">
      <li>collect information that can be used to identify you; for example, we never receive your name or address</li>
      <li>make any attempt to find out the identities of people visiting our website</li>
      <li>allow the tools that we use to find out the identities of people visiting our website</li>
      <li>sell, trade or give your details to third parties, unless we are required to do it by law</li>
    </ul>

    <p>All information we get through cookies and analytics will be treated in confidence.</p>
  </div>
</template>
